import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Bot as BotIcon, TrendingUp, DollarSign,
  Activity, Search, RefreshCw
} from 'lucide-react';
import { Bot, BotStatus, ValidationError, ValidationErrorResponse } from '../types/api';
import { botAPI } from '../services/api';
import BotCard from './BotCard';
import CreateBotModal from './CreateBotModal';
import RecoverableErrorModal from './RecoverableErrorModal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { cn, formatCurrency, formatPercentage } from '../lib/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

interface DashboardStats {
  totalBots: number;
  activeBots: number;
  totalProfit: number;
  avgReturn: number;
  totalVolume: number;
}

// Interface for recoverable error state
interface RecoverableErrorState {
  botId: number;
  botName: string;
  errors: ValidationError[];
}

const Dashboard: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'running' | 'stopped'>('all');
  const [loadingBots, setLoadingBots] = useState<{ [key: number]: 'starting' | 'stopping' | null }>({});
  const [refreshing, setRefreshing] = useState(false);
  const [recoverableErrors, setRecoverableErrors] = useState<RecoverableErrorState | null>(null);
  const queryClient = useQueryClient();

  // Fetch bots using React Query
  const { data: bots = [], isLoading: botsLoading } = useQuery({
    queryKey: ['bots'],
    queryFn: botAPI.getBots,
    refetchInterval: 30000, // Poll every 30 seconds
    staleTime: 0, // Always consider data stale for real-time updates
  });

  // Fetch bot statuses
  const { data: botStatuses = {} } = useQuery({
    queryKey: ['botStatuses', bots],
    queryFn: async () => {
      if (bots.length === 0) return {};

      const statusPromises = bots.map(async (bot) => {
        try {
          const response = await botAPI.getBotStatus(bot.id);
          return { botId: bot.id, status: response.runtime_status };
        } catch (error) {
          return { botId: bot.id, status: null };
        }
      });

      const results = await Promise.all(statusPromises);
      const statusMap: { [key: number]: BotStatus } = {};

      results.forEach(({ botId, status }) => {
        if (status) {
          statusMap[botId] = status;
        }
      });

      return statusMap;
    },
    enabled: bots.length > 0,
    refetchInterval: 5000, // Poll every 5 seconds
    staleTime: 0, // Always consider data stale for real-time updates
  });

  // Calculate dashboard statistics
  const stats: DashboardStats = React.useMemo(() => {
    const activeBots = Object.values(botStatuses).filter(status => status?.is_running).length;
    
    // Calculate aggregated stats from bot statuses
    let totalProfit = 0;
    let totalVolume = 0;
    let returnCount = 0;
    let totalReturn = 0;

    Object.values(botStatuses).forEach(status => {
      if (status?.portfolio_state) {
        totalProfit += status.portfolio_state.total_pnl || 0;
        totalVolume += status.portfolio_state.total_volume || 0;
        if (status.portfolio_state.return_percentage !== undefined) {
          totalReturn += status.portfolio_state.return_percentage;
          returnCount++;
        }
      }
    });

    return {
      totalBots: bots.length,
      activeBots,
      totalProfit,
      avgReturn: returnCount > 0 ? totalReturn / returnCount : 0,
      totalVolume,
    };
  }, [bots, botStatuses]);

  const statCards = [
    {
      title: 'Total Bots',
      value: stats.totalBots,
      icon: BotIcon,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      format: (v: number) => v.toString(),
    },
    {
      title: 'Active Bots',
      value: stats.activeBots,
      icon: Activity,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      format: (v: number) => v.toString(),
    },
    {
      title: 'Total Profit',
      value: stats.totalProfit,
      icon: DollarSign,
      color: stats.totalProfit >= 0 ? 'text-green-500' : 'text-red-500',
      bgColor: stats.totalProfit >= 0 ? 'bg-green-500/10' : 'bg-red-500/10',
      format: formatCurrency,
    },
    {
      title: 'Avg Return',
      value: stats.avgReturn,
      icon: TrendingUp,
      color: stats.avgReturn >= 0 ? 'text-green-500' : 'text-red-500',
      bgColor: stats.avgReturn >= 0 ? 'bg-green-500/10' : 'bg-red-500/10',
      format: formatPercentage,
    },
  ];

  const handleBotCreated = (newBot: Bot) => {
    queryClient.invalidateQueries({ queryKey: ['bots'] });
    setShowCreateModal(false);
    toast.success('Bot created successfully!');
  };

  const handleBotDeleted = (botId: number) => {
    queryClient.invalidateQueries({ queryKey: ['bots'] });
    toast.success('Bot deleted successfully!');
  };

  const handleBotUpdated = (updatedBot: Bot) => {
    queryClient.invalidateQueries({ queryKey: ['bots'] });
    toast.success('Bot updated successfully!');
  };

  // Poll bot status until it reaches the expected state
  const pollBotStatus = async (
    botId: number,
    expectedRunning: boolean,
    maxAttempts: number = 30,
    intervalMs: number = 1000
  ): Promise<boolean> => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await botAPI.getBotStatus(botId);
        const isRunning = response.runtime_status?.is_running ?? false;

        if (isRunning === expectedRunning) {
          return true;
        }

        // Check for error state when starting
        if (expectedRunning && response.runtime_status?.error_message) {
          return false;
        }
      } catch (error) {
        console.error('Error polling bot status:', error);
      }

      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    return false;
  };

  const handleBotStarted = async (botId: number, forceStart: boolean = false) => {
    setLoadingBots(prev => ({ ...prev, [botId]: 'starting' }));
    try {
      const response = await botAPI.startBot(botId, forceStart);

      // Show warnings if any
      if (response.warnings && response.warnings.length > 0) {
        response.warnings.forEach(warning => {
          toast(warning.message, { icon: '⚠️', duration: 5000 });
        });
      }

      // Poll until bot is actually running (max 30 seconds)
      const success = await pollBotStatus(botId, true, 30, 1000);

      queryClient.invalidateQueries({ queryKey: ['botStatuses'] });

      if (success) {
        toast.success('Bot started successfully!');
      } else {
        // Check for startup errors in status
        try {
          const statusResponse = await botAPI.getBotStatus(botId);
          const errorMsg = statusResponse.runtime_status?.error_message;
          if (errorMsg) {
            toast.error(`Bot failed to start: ${errorMsg}`);
          } else {
            toast.error('Bot failed to start. Check bot details for errors.');
          }
        } catch {
          toast.error('Bot failed to start. Check bot details for errors.');
        }
      }
    } catch (error: any) {
      console.error('Failed to start bot:', error);

      // Handle validation errors
      const errorData = error.response?.data?.detail as ValidationErrorResponse | string;

      if (typeof errorData === 'object' && errorData?.code) {
        if (errorData.code === 'RECOVERABLE_ERRORS') {
          // Find the bot name
          const bot = bots.find(b => b.id === botId);
          // Show modal letting user decide
          setRecoverableErrors({
            botId,
            botName: bot?.name || `Bot ${botId}`,
            errors: errorData.errors
          });
        } else if (errorData.code === 'VALIDATION_FAILED') {
          // Show detailed error toast for blocking errors
          const firstError = errorData.errors[0];
          toast.error(
            <div>
              <strong>{firstError.message}</strong>
              <p className="text-sm mt-1 opacity-80">{firstError.suggestion}</p>
            </div>,
            { duration: 6000 }
          );
        }
      } else if (typeof errorData === 'string') {
        toast.error(errorData);
      } else {
        toast.error('Failed to start bot');
      }
    } finally {
      setLoadingBots(prev => ({ ...prev, [botId]: null }));
    }
  };

  // Handle force start from recoverable error modal
  const handleForceStart = async () => {
    if (!recoverableErrors) return;

    const { botId } = recoverableErrors;
    setRecoverableErrors(null);

    // Start with force flag
    await handleBotStarted(botId, true);
  };

  // Close recoverable error modal
  const handleCloseRecoverableModal = () => {
    setRecoverableErrors(null);
  };

  const handleBotStopped = async (botId: number) => {
    setLoadingBots(prev => ({ ...prev, [botId]: 'stopping' }));
    try {
      await botAPI.stopBot(botId);

      // Poll until bot is actually stopped (max 15 seconds)
      const success = await pollBotStatus(botId, false, 15, 1000);

      queryClient.invalidateQueries({ queryKey: ['botStatuses'] });

      if (success) {
        toast.success('Bot stopped successfully!');
      } else {
        toast.error('Bot may not have stopped cleanly. Please check status.');
      }
    } catch (error) {
      console.error('Failed to stop bot:', error);
      toast.error('Failed to stop bot');
    } finally {
      setLoadingBots(prev => ({ ...prev, [botId]: null }));
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await queryClient.invalidateQueries();
    } finally {
      setRefreshing(false);
    }
  };


  const filteredBots = React.useMemo(() => {
    return bots.filter(bot => {
      // Filter by search query
      if (searchQuery && !bot.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      
      // Filter by status
      if (filterStatus !== 'all') {
        const status = botStatuses[bot.id];
        if (filterStatus === 'running' && !status?.is_running) return false;
        if (filterStatus === 'stopped' && status?.is_running) return false;
      }
      
      return true;
    });
  }, [bots, searchQuery, filterStatus, botStatuses]);

  if (botsLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-64" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="md:flex md:items-center md:justify-between mb-8"
      >
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-bold text-foreground">
            Trading Bots Dashboard
          </h1>
          <p className="mt-1 text-muted-foreground">
            Manage and monitor your automated trading bots
          </p>
        </div>
        <div className="mt-4 flex gap-2 md:mt-0 md:ml-4">
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            className="animate-in fade-in"
          >
            <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
          </Button>
          <Button
            onClick={() => setShowCreateModal(true)}
            variant="gradient"
            className="animate-in fade-in"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create New Bot
          </Button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8"
      >
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 + index * 0.05 }}
          >
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {stat.title}
                    </p>
                    <p className={cn("text-2xl font-bold mt-2", stat.color)}>
                      {stat.format(stat.value)}
                    </p>
                  </div>
                  <div className={cn("p-3 rounded-lg", stat.bgColor)}>
                    <stat.icon className={cn("h-6 w-6", stat.color)} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* Filters and Search */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex flex-col sm:flex-row gap-4 mb-6"
      >
        <div className="flex-1">
          <Input
            placeholder="Search bots..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<Search className="h-4 w-4" />}
            className="max-w-sm"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={filterStatus === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('all')}
          >
            All
          </Button>
          <Button
            variant={filterStatus === 'running' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('running')}
          >
            <Activity className="mr-2 h-4 w-4" />
            Running
          </Button>
          <Button
            variant={filterStatus === 'stopped' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('stopped')}
          >
            Stopped
          </Button>
        </div>
      </motion.div>

      {/* Bots Grid */}
      <AnimatePresence mode="popLayout">
        {filteredBots.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="text-center py-12"
          >
            <Card className="max-w-md mx-auto">
              <CardContent className="pt-12 pb-8">
                <BotIcon className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {searchQuery || filterStatus !== 'all' 
                    ? 'No bots found' 
                    : 'No trading bots yet'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {searchQuery || filterStatus !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Get started by creating your first trading bot.'}
                </p>
                {!searchQuery && filterStatus === 'all' && (
                  <Button onClick={() => setShowCreateModal(true)} variant="gradient">
                    <Plus className="mr-2 h-4 w-4" />
                    Create Your First Bot
                  </Button>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div 
            layout
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {filteredBots.map((bot, index) => (
              <motion.div
                key={bot.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.05 }}
              >
                <BotCard
                  bot={bot}
                  status={botStatuses[bot.id]}
                  onStart={() => handleBotStarted(bot.id)}
                  onStop={() => handleBotStopped(bot.id)}
                  onDelete={() => handleBotDeleted(bot.id)}
                  onUpdate={handleBotUpdated}
                  isStarting={loadingBots[bot.id] === 'starting'}
                  isStopping={loadingBots[bot.id] === 'stopping'}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Bot Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateBotModal
            onClose={() => setShowCreateModal(false)}
            onBotCreated={handleBotCreated}
          />
        )}
      </AnimatePresence>

      {/* Recoverable Error Modal */}
      <AnimatePresence>
        {recoverableErrors && (
          <RecoverableErrorModal
            botName={recoverableErrors.botName}
            errors={recoverableErrors.errors}
            onForceStart={handleForceStart}
            onClose={handleCloseRecoverableModal}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;